import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiClient';
import { fetchAccountsByContractId } from '../../services/accountsService';
import { addErrorNotification, addSuccessNotification } from './notificationSlice';
import {
    S3_ONBOARD_SOURCE_PATH,
    S3_SOURCE_PATH,
    S3_TEST_CONNECTION_PATH,
} from '../../constants/apiEndpoints';

const PAGE_SIZE = 10;

// ── Helpers ────────────────────────────────────────────────────────────────

const paginationFromResult = (result) => ({
    page: result.page,
    pageSize: result.pageSize,
    totalPages: result.totalPages,
    totalCount: result.totalCount,
    count: result.count,
});

// ── Thunks ─────────────────────────────────────────────────────────────────

export const fetchSources = createAsyncThunk(
    's3Sources/fetchSources',
    async ({ contractId, page = 1, pageSize = PAGE_SIZE }, { dispatch, rejectWithValue }) => {
        try {
            return await fetchAccountsByContractId(contractId, page, pageSize);
        } catch (error) {
            dispatch(addErrorNotification({
                message: error.message || 'Failed to load source accounts. Please try again.'
            }));
            return rejectWithValue(error.message || 'Failed to load source accounts.');
        }
    }
);

/**
 * Create (onboard) a new S3 source.
 * Rejects with the raw error object so the calling component can apply
 * field-level validation messages.
 */
export const createSource = createAsyncThunk(
    's3Sources/createSource',
    async ({ payload, contractId }, { rejectWithValue }) => {
        try {
            await apiFetch(S3_ONBOARD_SOURCE_PATH, {
                method: 'POST',
                body: JSON.stringify(payload),
                contentType: 'application/json',
                retry: false,
            });
            // Refresh list from page 1 after creation
            return await fetchAccountsByContractId(contractId, 1, PAGE_SIZE);
        } catch (error) {
            // Rethrow raw error — component handles field-level messages
            return rejectWithValue(error);
        }
    }
);


export const deleteSource = createAsyncThunk(
    's3Sources/deleteSource',
    async ({ sourceId, contractId, page = 1, accountId }, { dispatch, rejectWithValue }) => {
        try {
            await apiFetch(S3_SOURCE_PATH(sourceId), { method: 'DELETE' });
            dispatch(addSuccessNotification({
                message: `Deboarded source ${accountId || sourceId} successfully.`
            }));
            return await fetchAccountsByContractId(contractId, page, PAGE_SIZE);
        } catch (error) {
            dispatch(addErrorNotification({
                message: error.message || 'Failed to deboard source. Please try again.'
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Test connection for a source.
 * Returns `{ result, account, listResult }` on success so the component
 * can open its local modal with the response data.
 */
export const testSourceConnection = createAsyncThunk(
    's3Sources/testConnection',
    async ({ sourceId, account, contractId, page = 1 }, { dispatch, rejectWithValue }) => {
        try {
            const result = await apiFetch(
                S3_TEST_CONNECTION_PATH(sourceId),
                { method: 'POST', retry: false }
            );
            const listResult = await fetchAccountsByContractId(contractId, page, PAGE_SIZE);
            return { result, account, listResult };
        } catch (error) {
            dispatch(addErrorNotification({
                message: error.message || 'Failed to test connection. Please try again.'
            }));
            return rejectWithValue({
                error: error.message || 'Failed to test connection. Please try again.',
                account,
            });
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────

const initialPagination = {
    page: 1,
    pageSize: PAGE_SIZE,
    totalPages: 0,
    totalCount: 0,
    count: 0,
};

const initialState = {
    accounts: [],
    pagination: initialPagination,
    /** 'idle' | 'loading' | 'succeeded' | 'failed' */
    status: 'idle',
    error: null,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isTestingConnection: false,
};

const applyListResult = (state, result) => {
    state.accounts = result.accounts;
    state.pagination = paginationFromResult(result);
};

const s3SourcesSlice = createSlice({
    name: 's3Sources',
    initialState,
    reducers: {
        clearSources: () => initialState,
    },
    extraReducers: (builder) => {
        // ── fetchSources ──
        builder
            .addCase(fetchSources.pending, (state) => {
                state.status = 'loading';
                state.error = null;
                state.accounts = [];
                state.pagination = initialPagination;
            })
            .addCase(fetchSources.fulfilled, (state, action) => {
                state.status = 'succeeded';
                applyListResult(state, action.payload);
            })
            .addCase(fetchSources.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
                state.accounts = [];
            });

        // ── createSource ──
        builder
            .addCase(createSource.pending, (state) => {
                state.isCreating = true;
            })
            .addCase(createSource.fulfilled, (state, action) => {
                state.isCreating = false;
                applyListResult(state, action.payload);
            })
            .addCase(createSource.rejected, (state) => {
                state.isCreating = false;
            });

        // ── deleteSource ──
        builder
            .addCase(deleteSource.pending, (state) => {
                state.isDeleting = true;
            })
            .addCase(deleteSource.fulfilled, (state, action) => {
                state.isDeleting = false;
                applyListResult(state, action.payload);
            })
            .addCase(deleteSource.rejected, (state) => {
                state.isDeleting = false;
            });

        // ── testSourceConnection ──
        builder
            .addCase(testSourceConnection.pending, (state) => {
                state.isTestingConnection = true;
            })
            .addCase(testSourceConnection.fulfilled, (state, action) => {
                state.isTestingConnection = false;
                applyListResult(state, action.payload.listResult);
            })
            .addCase(testSourceConnection.rejected, (state) => {
                state.isTestingConnection = false;
            });
    },
});

export const { clearSources } = s3SourcesSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────

export const selectSources = (state) => state.s3Sources.accounts;
export const selectSourcesPagination = (state) => state.s3Sources.pagination;
export const selectSourcesStatus = (state) => state.s3Sources.status;
export const selectSourcesError = (state) => state.s3Sources.error;
export const selectIsCreatingSource = (state) => state.s3Sources.isCreating;
export const selectIsUpdatingSource = (state) => state.s3Sources.isUpdating;
export const selectIsDeletingSource = (state) => state.s3Sources.isDeleting;
export const selectIsTestingConnection = (state) => state.s3Sources.isTestingConnection;

export default s3SourcesSlice.reducer;
